import { useEffect, useState } from "react";

import { AppointmentTypeLabel } from "@/constants/Appointment";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { useLazyGetAppointmentsWithOptionsQuery } from "@/store/api/schedule/schedule";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { isWhyCompleteAssessmentsAccordionVisible } from "@/utils/assessment.util";

interface UseWhyCompleteAssessmentsParams {
  tasks: Record<string, TaskItem[]>;
}

interface UseWhyCompleteAssessmentsProps {
  isWhyCompleteAssessmentsLoading: boolean;
  isWhyCompleteAssessmentsVisible: boolean;
}

const useWhyCompleteAssessments = ({
  tasks,
}: UseWhyCompleteAssessmentsParams): UseWhyCompleteAssessmentsProps => {
  const { isBaselineAssessmentsEnabled } = useFeatureFlags();
  const appUser = useAppUserSelector();
  const { externalId: appUserId, isPatient } = appUser;
  const [allIntakeAppts, setAllIntakeAppts] = useState<
    AppointmentDetailResponse[]
  >([]);
  const [getAppointments, { isLoading: isLoadingAppointments }] =
    useLazyGetAppointmentsWithOptionsQuery();

  useEffect(() => {
    if (!isBaselineAssessmentsEnabled || !isPatient || !appUserId) return;
    void getAppointments({
      appointmentTypes: [
        AppointmentTypeLabel.DietitianIntake,
        AppointmentTypeLabel.TherapyIntake,
      ],
      isPatient,
      pageNumber: 1,
      pageSize: 30,
      patientExternalId: appUserId,
    })
      .unwrap()
      .then((response: GetAppointmentEventsResponse) => {
        setAllIntakeAppts(response.data);
      })
      .catch(() => {
        setAllIntakeAppts([]);
      });
  }, [getAppointments, isPatient, appUserId, isBaselineAssessmentsEnabled]);

  if (!isBaselineAssessmentsEnabled) {
    return {
      isWhyCompleteAssessmentsLoading: false,
      isWhyCompleteAssessmentsVisible: false,
    };
  }

  const isVisible = isWhyCompleteAssessmentsAccordionVisible({
    appointments: allIntakeAppts,
    tasks,
  });

  return {
    isWhyCompleteAssessmentsLoading: isLoadingAppointments,
    isWhyCompleteAssessmentsVisible: isVisible,
  };
};

export type { UseWhyCompleteAssessmentsProps };
export default useWhyCompleteAssessments;
